import { useRef, useEffect, useState, useMemo } from 'react';

import type { Prompt, Variant } from '../../../interface/Prompt';
import { Probe } from '../../../interface/Probe';
import type {
    RepeatAudioCallbacks,
    InterruptAudioCallbacks,
    EncourageAudioCallbacks,
    PlayVariantAudioCallbacks,
    PlayPromptAudioCallbacks,
} from '../../../interface/PlayAudio';
import { useAudioPlayer } from './useAudioPlayer';
import { PartRules, RepeatRule } from '../../../interface/Configuration';
import { pushFaroEvent } from '../../../faroSession';

interface Options {
    currentPrompt: Prompt;
    probeList: Probe[];
    currentPartRules: PartRules;
}

const MAX_VARIANT_PLAY = 1; // Maximum variant plays for a prompt

export const useAudioActions = ({ currentPrompt, probeList, currentPartRules }: Options) => {
    const { playAudio, audioRef, pause, isPlayingRef, resume, isPausedRef } = useAudioPlayer();

    const [error, setError] = useState<string | null>(null);

    const currentRepeatableAudioRef = useRef({
        audioUrl: '',
        type: '' as RepeatRule['type'],
        audioPath: '',
    });

    const encourageCounterRef = useRef(0);
    const simplifiedVariantCounterRef = useRef(0);
    const backupVariantCounterRef = useRef(0);

    const encourageRef = useRef(false);
    const currentEncourageIdx = useRef(0);

    const repeatCountersRef = useRef<Record<RepeatRule['type'], number>>({
        ENCOURAGE: 0,
        PROMPT: 0,
        VARIANT_SIMPLIFIED: 0,
        VARIANT_BACKUP: 0,
    });

    const { shortResponse, repeat: rpt } = currentPartRules;
    const MAX_ENCROURAGE = shortResponse.maxEncourage; // Maximum encourages for a prompt
    const partProbeList = probeList.filter((probe) => probe.parts.includes(currentPrompt?.partName));

    const encourageProbes = useMemo(() => partProbeList.filter((pb) => pb.type === 'ENCOURAGE'), [partProbeList]);

    // Play prompt
    const playPrompt = (callbacks: PlayPromptAudioCallbacks, force?: boolean) => {
        if (!currentPrompt) return;

        const { onPlay, onEnded, onError, onCanPlayThrough } = callbacks;

        playAudio(currentPrompt.audioPreSignUrl, {
            force,
            onPlay: () => {
                onPlay?.(currentPrompt.audioPath);
            },
            onEnded: () => {
                currentRepeatableAudioRef.current = {
                    audioPath: currentPrompt.audioPath,
                    audioUrl: currentPrompt.audioPreSignUrl,
                    type: 'PROMPT',
                };
                onEnded?.();
            },
            onError: (error) => {
                console.error('Error playing prompt audio:', error);
                setError('Error playing prompt audio');
                onError?.(error);
            },
            onCanPlayThrough: () => {
                onCanPlayThrough?.();
            },
        });
    };

    // Repeat current prompt
    const repeat = (callbacks: RepeatAudioCallbacks) => {
        const probe = partProbeList.find((pb) => pb.type === 'REPEAT');
        if (!probe) return;

        const { onPlay, onEnded, onError, onMaxRepeat, onRepeat } = callbacks;

        const audioType = currentRepeatableAudioRef.current.type;
        const maxRepeat = rpt.rules.find((mr) => mr.type === audioType)?.maxRepeat!;
        const currentRepeatCounter = repeatCountersRef.current[audioType];

        if (!isPlayingRef.current && currentRepeatCounter >= maxRepeat) {
            onMaxRepeat?.(audioType);
        } else if (audioType === 'ENCOURAGE') {
            playAudio(currentRepeatableAudioRef.current.audioUrl, {
                onPlay: () => {
                    repeatCountersRef.current[audioType] += 1;
                    onRepeat?.(currentRepeatableAudioRef.current.audioPath, audioType);
                },
                onEnded,
            });
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    repeatCountersRef.current[audioType] += 1;
                    onPlay?.(probe.audioPath);
                },
                onEnded: () => {
                    playAudio(currentRepeatableAudioRef.current.audioUrl, {
                        onPlay: () => {
                            onRepeat?.(currentRepeatableAudioRef.current.audioPath, audioType);
                        },
                        onEnded,
                        force: true,
                    });
                },
                onError: (error) => {
                    console.error('Error playing repeat probe audio:', error);
                    setError('Error playing repeat probe audio');
                    onError?.(error);
                },
            });
        }
    };

    // Interrupt
    const interrupt = (callbacks: InterruptAudioCallbacks) => {
        const probe = partProbeList.find((pb) => pb.type === 'INTERRUPT');

        if (!probe) return;

        const { onPlay, onEnded, onError } = callbacks;

        playAudio(probe.audioPreSignUrl, {
            onPlay: () => {
                onPlay?.(probe.audioPath);
            },
            onEnded,
            onError: (error) => {
                console.error('Error playing interrupt probe audio:', error);
                setError('Error playing interrupt probe audio');
                onError?.(error);
            },
        });
    };

    const questionNavigator = (callbacks: InterruptAudioCallbacks) => {
        const probe = partProbeList.find((pb) => pb.type === 'MOVE_ON');

        if (!probe) return;

        const { onPlay, onEnded, onError } = callbacks;

        playAudio(probe.audioPreSignUrl, {
            onPlay: () => {
                onPlay?.(probe.audioPath);
            },
            onEnded,
            onError: (error) => {
                console.error('Error playing move-on probe audio:', error);
                setError('Error playing move-on probe audio');
                onError?.(error);
            },
        });
    };

    // Encourage
    const encourage = (callbacks: EncourageAudioCallbacks) => {
        const probe = encourageProbes[currentEncourageIdx.current];

        if (!probe) return;

        const { onPlay, onEnded, onError, onMaxEncourage } = callbacks;

        if (!isPlayingRef.current && encourageCounterRef.current >= MAX_ENCROURAGE) {
            onMaxEncourage?.();
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    repeatCountersRef.current.ENCOURAGE = 0;
                    currentEncourageIdx.current = (currentEncourageIdx.current + 1) % encourageProbes.length;
                    encourageCounterRef.current += 1;

                    onPlay?.(probe.audioPath);
                },
                onEnded: () => {
                    currentRepeatableAudioRef.current = {
                        audioPath: probe.audioPath,
                        audioUrl: probe.audioPreSignUrl,
                        type: 'ENCOURAGE',
                    };
                    onEnded?.();
                },
                onError: (error) => {
                    console.error('Error playing encourage probe audio:', error);
                    setError('Error playing encourage probe audio');
                    onError?.(error);
                },
            });
        }
    };

    // Play variant
    const playSimplifiedVariant = (callbacks: PlayVariantAudioCallbacks) => {
        const { onPlay, onEnded, onError, onMaxVariantsPlayed, onPlayVariant } = callbacks;
        const variant = currentPrompt?.variants?.find((v: Variant) => v.type === 'SIMPLIFIED');

        if (!variant)
            return repeat({ onPlay, onEnded, onError, onMaxRepeat: onMaxVariantsPlayed, onRepeat: onPlayVariant });

        const probe = partProbeList.find((pb) => pb.type === 'REWORD');

        if (!probe) return;

        if (!isPlayingRef.current && simplifiedVariantCounterRef.current >= MAX_VARIANT_PLAY) {
            onMaxVariantsPlayed?.();
        } else {
            playAudio(probe.audioPreSignUrl, {
                onPlay: () => {
                    simplifiedVariantCounterRef.current += 1;
                    onPlay?.(probe.audioPath);
                },
                onEnded: () => {
                    playAudio(variant.audioPreSignUrl, {
                        force: true,
                        onPlay: () => {
                            onPlayVariant?.(variant.audioPath);
                        },
                        onEnded: () => {
                            currentRepeatableAudioRef.current = {
                                audioPath: variant.audioPath,
                                audioUrl: variant.audioPreSignUrl,
                                type: 'VARIANT_SIMPLIFIED',
                            };
                            onEnded?.();
                        },
                    });
                },
                onError: (error) => {
                    console.error('Error playing variant audio:', error);
                    setError('Error playing variant audio');
                    onError?.(error);
                },
            });
        }
    };

    const playBackupVariant = (callbacks: PlayVariantAudioCallbacks) => {
        const { onPlay, onEnded, onError, onMaxVariantsPlayed, onPlayVariant } = callbacks;
        const variant = currentPrompt?.variants?.find((v: Variant) => v.type === 'BACKUP');

        if (!variant)
            return repeat({ onPlay, onEnded, onError, onMaxRepeat: onMaxVariantsPlayed, onRepeat: onPlayVariant });

        if (backupVariantCounterRef.current >= MAX_VARIANT_PLAY) {
            onMaxVariantsPlayed?.();
        } else {
            playAudio(variant.audioPreSignUrl, {
                onPlay: () => {
                    backupVariantCounterRef.current += 1;
                    encourageCounterRef.current += 1; //back up variant counts as encourage. Reference R3
                    onPlayVariant?.(variant.audioPath);
                },
                onEnded: () => {
                    currentRepeatableAudioRef.current = {
                        audioPath: variant.audioPath,
                        audioUrl: variant.audioPreSignUrl,
                        type: 'VARIANT_BACKUP',
                    };
                    onEnded?.();
                },
                onError: (error) => {
                    console.error('Error playing variant audio:', error);
                    setError('Error playing variant audio');
                    onError?.(error);
                },
            });
        }
    };

    const reset = () => {
        repeatCountersRef.current = {
            ENCOURAGE: 0,
            PROMPT: 0,
            VARIANT_SIMPLIFIED: 0,
            VARIANT_BACKUP: 0,
        };
        encourageCounterRef.current = 0;
        encourageRef.current = false;
        simplifiedVariantCounterRef.current = 0;
        backupVariantCounterRef.current = 0;
    };

    const playBeep = (callbacks: PlayPromptAudioCallbacks) => {
        playAudio('/assets/audios/beep.mp3', {
            onEnded: () => {
                pushFaroEvent('beep_audio_ended');
                callbacks.onEnded?.();
            },
            onError: (error) => {
                console.error('Error playing beep audio:', error);
                setError('Error playing beep audio');
            },
        });
    };

    useEffect(() => {
        return () => {
            currentEncourageIdx.current = 0;
            reset();
        };
    }, []);

    return {
        encourage,
        repeat,
        interrupt,
        playSimplifiedVariant,
        playPrompt,
        resetRefs: reset,
        pause,
        resume,
        playBeep,
        encourageRef,
        audioRef,
        isPlayingRef,
        isPausedRef,
        error,
        playBackupVariant,
        questionNavigator,
        encourageCounterRef,
    };
};
